import { Box, ButtonBase, styled } from "@mui/material";

// Machine-authored metadata — change labels, counters — is set in mono so it
// reads as annotation rather than as the editor's own prose. A stack, not a
// webfont: no network request, nothing new to install.
export const MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

// Machine-authored annotation — scope line, change counters, the composer hint.
export const LABEL = {
  fontFamily: MONO,
  fontSize: "0.6875rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
};

export const PANEL_WIDTH = 400;

// Written out as @keyframes rather than built with emotion's keyframes():
// these components style themselves through a function that returns a CSS
// string, and a keyframes object interpolated into a string is toString()'d
// into an _EMO_ marker that emotion only ever resolves for object styles — so
// the animation silently never runs. Stylis hoists these to the top level, and
// the names are global, hence the prefix (MarkdownEditor already owns `blink`).

/**
 * The assistant's shell. A card in the editor's own flex row — same border and
 * radius as the app's content frame, so the two read as one surface rather than
 * a drawer parked on top of the page.
 *
 * Under md there isn't room for two columns, so `.floating` lifts the same card
 * over the editor and the EdgeTab below takes over opening it.
 */
export const Panel = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-height: 0;
  width: ${PANEL_WIDTH}px;
  overflow: hidden;
  border: 1px solid ${theme.palette.borderColor.default};
  border-radius: 12px;
  background: ${theme.palette.background.paper};

  &.floating {
    position: absolute;
    inset-block: 0;
    inset-inline-end: 0;
    z-index: 3;
    width: min(${PANEL_WIDTH}px, calc(100% - 24px));
    box-shadow: ${theme.shadows[8]};
    animation: assistantSlideIn 200ms ease both;
  }

  @keyframes assistantSlideIn {
    from { opacity: 0; transform: translateX(12px); }
    to   { opacity: 1; transform: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    &.floating { animation: none; }
  }
`
);

/** Collapsed handle for the floating panel — icon only, so it needs no translation. */
export const EdgeTab = styled(ButtonBase)(
  ({ theme }) => `
  position: absolute;
  inset-inline-end: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  padding: 14px 6px;
  color: ${theme.palette.text.secondary};
  background: ${theme.palette.background.paper};
  border: 1px solid ${theme.palette.borderColor.default};
  border-inline-end: none;
  border-start-start-radius: 10px;
  border-end-start-radius: 10px;

  &:hover {
    color: ${theme.palette.text.primary};
    background: ${theme.palette.background.secondary};
  }
`
);

/**
 * Input block. The field and the send button share one border that reacts to
 * focus, so the composer reads as a single control instead of a row of parts.
 */
export const Composer = styled(Box)(
  ({ theme }) => `
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 6px 6px 12px;
  border: 1px solid ${theme.palette.borderColor.dark};
  border-radius: 12px;
  background: ${theme.palette.background.default};
  transition: border-color 160ms ease;

  &:focus-within {
    border-color: ${theme.palette.text.disabled};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
);

/**
 * A single proposed change. The heavy inline-start border is a copy-editor's
 * change bar: it marks the margin of an altered passage, and being a logical
 * border it moves to the right-hand edge in Hebrew on its own.
 */
export const ChangeCard = styled(Box)(
  ({ theme }) => `
  animation: assistantRise 260ms ease both;

  @keyframes assistantRise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: none; }
  }

  padding: 10px 12px;
  border: 1px solid ${theme.palette.borderColor.default};
  border-inline-start: 2px solid ${theme.palette.text.primary};
  border-radius: ${theme.shape.borderRadius};
  background: ${theme.palette.background.paper};

  &.stale {
    border-inline-start-color: ${theme.palette.text.disabled};
    opacity: 0.6;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
);

export const Dot = styled("span")(
  ({ theme }) => `
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${theme.palette.text.secondary};
  animation: assistantBlink 1.3s infinite ease-in-out;

  @keyframes assistantBlink {
    0%, 80%, 100% { opacity: 0.2; }
    40%           { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
);

/**
 * "Thinking" while a reply is in flight. Same mono annotation scale as the
 * panel's other machine-authored labels; the light passing across it is the
 * only thing on the surface that moves on its own, which is what makes the wait
 * read as work rather than as a stall. Its period is 1.5× the dots' blink, so
 * the two settle back into step every three blinks instead of drifting.
 */
export const ThinkingLabel = styled(Box)(
  ({ theme }) => `
  font-family: ${MONO};
  font-size: 0.6875rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: linear-gradient(
    90deg,
    ${theme.palette.text.secondary} 40%,
    ${theme.palette.text.primary} 50%,
    ${theme.palette.text.secondary} 60%
  );
  background-size: 300% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: assistantSweep 1.95s linear infinite ${
    theme.direction === "rtl" ? "reverse" : ""
  };

  /* The highlight sits mid-gradient, three times the label's width, so at both
     ends of the sweep it is parked off the text and the loop cannot pop. */
  @keyframes assistantSweep {
    from { background-position: 100% 0; }
    to   { background-position: 0% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: none;
    color: ${theme.palette.text.secondary};
  }
`
);
